import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { MessageCircle, AlertTriangle, Sparkles, Clock } from 'lucide-react';
import api from '../services/api';
import { getHomePathForRole } from '../utils/roleHome';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, currentUser, loading: authLoading, sessionReplacedMsg, deployUpdateMsg, deployNotes } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [maintenanceSecondsLeft, setMaintenanceSecondsLeft] = useState(0);

  // ── Maintenance window check ───────────────────────────────────────────────
  // Fetch /version on mount. If maintenance_until is in the future, show a
  // countdown and disable Sign In until the window passes.
  useEffect(() => {
    let timer;
    const checkMaintenance = async () => {
      try {
        const res = await api.get('/version');
        const raw = res.data?.maintenance_until;
        if (!raw) return;
        // Parse ISO 8601 timestamp. Python isoformat() returns "+00:00" (not "Z"), so
        // we must NOT blindly append "Z" — just use new Date() which handles both forms.
        const until = new Date(raw);
        const secondsLeft = Math.ceil((until.getTime() - Date.now()) / 1000);
        if (secondsLeft <= 0) return;
        setMaintenanceSecondsLeft(secondsLeft);
        timer = setInterval(() => {
          const remaining = Math.ceil((until.getTime() - Date.now()) / 1000);
          if (remaining <= 0) {
            setMaintenanceSecondsLeft(0);
            clearInterval(timer);
          } else {
            setMaintenanceSecondsLeft(remaining);
          }
        }, 1000);
      } catch { /* best-effort */ }
    };
    checkMaintenance();
    return () => clearInterval(timer);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  // If the user is already authenticated, redirect to their home page.
  useEffect(() => {
    if (!authLoading && currentUser) {
      const returnTo = location.state?.returnTo;
      navigate(returnTo || getHomePathForRole(currentUser.role), { replace: true });
    }
  }, [currentUser, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast({ title: 'Welcome back!', description: 'Login successful', duration: 1500 });

      const returnTo = location.state?.returnTo;
      if (returnTo) {
        navigate(returnTo);
        setSubmitting(false);
        return;
      }

      navigate(getHomePathForRole(result.user?.role));
    } else {
      toast({
        title: 'Login failed',
        description: result.error || 'Invalid credentials',
        variant: 'destructive',
      });
    }

    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-4">
      <div className="w-full max-w-md p-6 md:p-8 bg-white rounded-2xl shadow-xl">
        <div className="flex flex-col items-center mb-6 md:mb-8">
          <div className="w-14 h-14 md:w-16 md:h-16 bg-emerald-500 rounded-full flex items-center justify-center mb-3 md:mb-4">
            <MessageCircle className="w-7 h-7 md:w-8 md:h-8 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">OrderChat</h1>
          <p className="text-gray-500 mt-1 md:mt-2 text-sm md:text-base">Sign in to your account</p>
        </div>

        {/* ── New deployment "What's New" card ─────────────────────────── */}
        {deployUpdateMsg && (
          <div
            className="mb-5 rounded-xl overflow-hidden border border-emerald-200"
            data-testid="deploy-update-banner"
          >
            {/* Header */}
            <div className="bg-emerald-500 px-4 py-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-white flex-shrink-0" />
              <div>
                <p className="text-white font-semibold text-sm leading-tight">
                  DiaForge has been updated!
                </p>
                <p className="text-emerald-100 text-xs mt-0.5">
                  Please log in to continue
                </p>
              </div>
            </div>

            {/* What's new list */}
            {deployNotes && deployNotes.length > 0 && (
              <div className="bg-emerald-50 px-4 py-3">
                <p className="text-emerald-700 text-xs font-semibold uppercase tracking-wide mb-2">
                  What's new
                </p>
                <ul className="space-y-1.5">
                  {deployNotes.map((note, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5 flex-shrink-0 text-xs">✦</span>
                      <span className="text-emerald-800 text-xs leading-snug">{note}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        {/* ──────────────────────────────────────────────────────────────── */}

        {/* Session replaced warning */}
        {sessionReplacedMsg && (
          <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-3" data-testid="session-replaced-banner">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm">{sessionReplacedMsg}</p>
          </div>
        )}

        {/* ── Maintenance / deployment lock banner ─────────────────────── */}
        {maintenanceSecondsLeft > 0 && (
          <div
            className="mb-5 rounded-xl overflow-hidden border border-orange-200"
            data-testid="maintenance-banner"
          >
            <div className="bg-orange-500 px-4 py-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-white flex-shrink-0 animate-pulse" />
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Update in Progress</p>
                <p className="text-orange-100 text-xs mt-0.5">Login will be available shortly</p>
              </div>
              <div className="ml-auto bg-orange-600 text-white text-sm font-bold px-3 py-1.5 rounded-lg min-w-[52px] text-center">
                {maintenanceSecondsLeft}s
              </div>
            </div>
            <div className="bg-orange-50 px-4 py-2.5">
              <p className="text-orange-700 text-xs leading-snug">
                A new version of DiaForge is being deployed. Please wait a moment before logging in to ensure everything is stable.
              </p>
            </div>
          </div>
        )}
        {/* ──────────────────────────────────────────────────────────────── */}

        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
            disabled={submitting || maintenanceSecondsLeft > 0}
            data-testid="login-submit-btn"
          >
            {submitting
              ? 'Signing in...'
              : maintenanceSecondsLeft > 0
              ? `Update in progress (${maintenanceSecondsLeft}s)`
              : 'Sign In'}
          </Button>
        </form>

        <p className="text-center text-gray-600 mt-4 md:mt-6 text-sm md:text-base">
          Don't have an account?{' '}
          <Link to="/signup" className="text-emerald-500 hover:text-emerald-600 font-medium">
            Sign up
          </Link>
        </p>

        {/* Demo credentials removed */}
      </div>
    </div>
  );
};

export default Login;
